/**
 * ================================================================
 * PROFILE SERVICE - Unified Service (Phase 1 Refactor)
 * ================================================================
 *
 * Single entry point for all User Profile operations.
 * - Replaces profile.api.ts and profile.client.ts
 * - Uses Strict Zod Types from @mebi/schemas
 * - Implements Sanitzation for Blob URLs
 * - Stateless: Relies on TanStack Query for caching
 *
 * @module ProfileService
 */

import {
  usersControllerGetProfile,
  usersControllerSetupProfile,
  usersControllerUpdateProfile,
  usersControllerUpdatePlayerProfile,
  usersControllerUpdateOrganizerProfile,
  customInstance,
} from "@mebi/api-client";
import { UploadService, getR2PublicUrl } from "@/storage/uploadService";
import {
  account as User,
  userSchema,
  players as Player,
  playersSchema,
  organizers as Organizer,
  organizersSchema,
} from "@mebi/schemas";

// ============================================================================
// Types
// ============================================================================

interface RawUser {
  id?: string;
  email?: string;
  emailVerified?: boolean;
  phoneNumber?: string;
  phoneNumberVerified?: boolean | null;
  full_name?: string;
  name?: string;
  city?: string;
  ward?: string;
  gender?: string;
  birth_date?: string | Date;
}

interface RawProfile {
  id?: string;
  full_name?: string;
  phoneNumber?: string;
  emailVerified?: boolean;
  phoneNumberVerified?: boolean | null;
  city?: string;
  ward?: string;
  gender?: string;
  birth_date?: string;
}

interface RawPlayer {
  nickname?: string;
  avatar_url?: string;
}

interface RawOrganizer {
  name?: string | null;
  display_name?: string | null;
  logo_url?: string | null;
  verified_status?: string | null;
}

// Basic Role Type (to match AuthStore)
export type UserRole = "Player" | "Organizer";

export interface UserProfile {
  id: string;
  email: string;
  emailVerified?: boolean;
  fullName: string;
  nickname?: string;
  phoneNumber?: string;
  phoneNumberVerified?: boolean;
  avatarUrl?: string; // Player avatar
  organizerAvatarUrl?: string; // Organizer logo
  organizerName?: string;
  organizerDisplayName?: string;
  roles: string[];

  // Raw Schema Data
  authUser?: User;
  player?: Player;
  organizer?: Organizer; // This is the Organizer Persona

  // UI Helpers
  userInfoWithRoles?: unknown; // Legacy compatibility
  requiresProfileSetup?: boolean;
  city?: string;
  ward?: string;
  gender?: string;
  birthDate?: string;
  // bio populated above at line 181
}

export interface RoleDisplayInfo {
  playerName: string;
  playerAvatarUrl?: string;
  organizerName: string;
  organizerAvatarUrl?: string;
}

export interface UpdateProfileRequest {
  // Common
  fullName?: string;
  phone?: string;
  email?: string;
  dateOfBirth?: string;
  gender?: string;
  city?: string;
  ward?: string;

  // Player
  nickname?: string;
  bio?: string;
  avatarUrl?: string;

  // Organizer
  organizerName?: string;
  organizerDisplayName?: string;
  organizerLogoUrl?: string;
  organizationType?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string; // Physical address
  contactPerson?: string;
  contactPosition?: string;
  licenseType?: string;
  verificationNote?: string;
  organizerDescription?: string;
  slug?: string;
  fanpageUrl?: string;
  websiteUrl?: string;
}

export interface ProfileApiResult<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

const hasAnyOwnField = <K extends keyof UpdateProfileRequest>(
  data: UpdateProfileRequest,
  fields: readonly K[],
): boolean =>
  fields.some((field) => Object.prototype.hasOwnProperty.call(data, field));

// ============================================================================
// Service
// ============================================================================

export const profileService = {
  /**
   * Get Safe Roles (Synchronous Sanitization)
   * Removes 'Organizer' if verification status is not verified.
   */
  getSafeRoles(user: UserProfile | null | undefined): UserRole[] {
    if (!user) return ["Player"];
    const rawRoles = Array.isArray(user.roles) ? user.roles : [];
    const normalizedRoles = rawRoles
      .map((role) => String(role).trim())
      .filter((role) => role.length > 0)
      .map(
        (role) =>
          (role.charAt(0).toUpperCase() +
            role.slice(1).toLowerCase()) as UserRole,
      )
      .filter((role) => role === "Player" || role === "Organizer");

    let roles = [...normalizedRoles];
    if (!roles.includes("Player")) {
      roles.unshift("Player");
    }

    if (
      roles.includes("Organizer") &&
      user.organizer?.verified_status !== "verified"
    ) {
      roles = roles.filter((role) => role !== "Organizer");
    }

    if (roles.length === 0) return ["Player"];
    return roles;
  },

  getAvailableRoles(
    user: UserProfile,
    isOrganizerVerified?: boolean | null,
  ): UserRole[] {
    let roles = [...this.getSafeRoles(user)];

    if (!roles.includes("Player")) {
      roles.unshift("Player");
    }

    if (isOrganizerVerified === true && !roles.includes("Organizer")) {
      roles.push("Organizer");
    }

    if (isOrganizerVerified === false) {
      roles = roles.filter((role) => role !== "Organizer");
    }

    return roles;
  },

  getRoleDisplayInfo(user: UserProfile): RoleDisplayInfo {
    const playerName =
      user.nickname?.trim() || user.fullName?.trim() || "Người chơi";
    const userId = user.id;
    const playerAvatarPath = user.player?.avatar_url || user.avatarUrl;
    const playerAvatarUrl =
      playerAvatarPath && userId
        ? this.getAvatarUrlWithStructure(userId, "Player", playerAvatarPath) ||
          undefined
        : undefined;

    const organizerName =
      user.organizer?.display_name?.trim() ||
      user.organizer?.name?.trim() ||
      user.organizerDisplayName?.trim() ||
      user.organizerName?.trim() ||
      "Tổ chức viên";

    const organizerLogoPath =
      user.organizer?.logo_url?.trim() || user.organizerAvatarUrl?.trim();
    const organizerAvatarUrl =
      organizerLogoPath && userId
        ? this.getAvatarUrlWithStructure(
            userId,
            "Organizer",
            organizerLogoPath,
          ) || undefined
        : undefined;

    return {
      playerName,
      playerAvatarUrl,
      organizerName,
      organizerAvatarUrl,
    };
  },

  /**
   * Get basic user info (GET /users/me)
   */
  async getMe(): Promise<ProfileApiResult<User>> {
    try {
      // Using usersControllerGetProfile as /users/me is not available in Orval
      const { data } = await usersControllerGetProfile();
      const rawData = data as unknown as { user?: User };

      return {
        success: true,
        message: "Success",
        data: rawData.user,
      };
    } catch (error: unknown) {
      const err = error as { message: string };
      return {
        success: false,
        message: "Cannot get user info",
        error: err.message,
      };
    }
  },

  /**
   * Get Full Profile (Aggregated)
   * SSOT for Roles and Verification Status
   */
  async getCurrentUserProfile(): Promise<UserProfile> {
    try {
      const response = await usersControllerGetProfile();
      // Cast response to expected structure with data property (runtime behavior of custom-instance)
      const data = (response as unknown as { data: unknown }).data;

      const rawData = data as {
        user?: RawUser;
        player?: RawPlayer;
        organizer?: RawOrganizer;
        roles?: unknown;
        profile?: RawProfile;
      };

      // Validate Logic using Zod Schemas
      // We use safeParse to avoid crashing entirely if one field is off,
      // but ideally we should use parse() for strictness as requested.
      // Using parse() here to enforce contract.

      const rawUser = rawData.user || {};
      const rawPlayer = rawData.player;
      // Handle potential backend inconsistency (organizer vs organization)
      const rawOrganizer =
        rawData.organizer ||
        (rawData as { organization?: Organizer }).organization;
      const rawRoles = rawData.roles;

      // Validate User
      // API response is partial/DTO, so we must use partial schema.
      // Use safeParse to avoid Date serialization issues
      let user: RawUser;
      const parsedUser = userSchema.partial().safeParse(rawUser);
      if (parsedUser.success) {
        user = parsedUser.data as RawUser;
      } else {
        console.warn(
          "[ProfileService] User schema validation failed (using raw data):",
          parsedUser.error,
        );
        user = rawUser;
      }

      if (!user.id || !user.email) {
        console.error("[ProfileService] Critical user info missing", user);
        throw new Error("Invalid User Profile: Missing ID or Email");
      }

      // Validate Player (nullable)
      // Use safeParse to handle Date/String serialization issues from JSON response
      let player: RawPlayer | null = null;
      if (rawPlayer) {
        const parsed = playersSchema.partial().safeParse(rawPlayer);
        if (parsed.success) {
          player = parsed.data as RawPlayer;
        } else {
          console.warn(
            "[ProfileService] Player schema validation failed (using raw data):",
            parsed.error,
          );
          player = rawPlayer; // Fallback to raw data
        }
      }

      // Validate Organizer (nullable)
      let organizer: RawOrganizer | null = null;
      if (rawOrganizer) {
        const parsed = organizersSchema.partial().safeParse(rawOrganizer);
        if (parsed.success) {
          organizer = parsed.data;
        } else {
          console.warn(
            "[ProfileService] Organizer schema validation failed (using raw data):",
            parsed.error,
          );
          organizer = rawOrganizer; // Fallback to raw data
        }
      }

      const roles = rawRoles; // Roles schema not strictly defined yet in Prisma exports, keeping as is or use array validation if needed.

      const profile = rawData.profile; // Keep legacy profile for now just in case, but data comes from user.

      // Construct Unified Profile
      return this.constructUserProfile(user, player, organizer, roles, profile);
    } catch (error: unknown) {
      // Detailed error logging for debugging device issues
      const err = error as {
        message: string;
        code?: string;
        name?: string;
        response?: {
          status?: number;
          statusText?: string;
          data?: unknown;
        };
      };
      const errorDetails = {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        code: err.code,
        name: err.name,
      };
      console.error(
        "[ProfileService] Error getting current profile:",
        JSON.stringify(errorDetails),
      );
      throw error;
    }
  },

  determineUserRoles(
    roles: unknown,
    organizer: RawOrganizer | null,
  ): UserRole[] {
    const baseRoles =
      Array.isArray(roles) && roles.length > 0
        ? (roles as UserRole[])
        : ["Player" as UserRole];

    // Ensure "Player" is always present (default role)
    if (!baseRoles.includes("Player")) {
      baseRoles.push("Player");
    }

    // Infer "Organizer" role if organizer profile exists
    if (organizer && !baseRoles.includes("Organizer")) {
      baseRoles.push("Organizer");
    }

    return baseRoles.filter((role) => {
      // Filter Organizer if not verified
      if (role === "Organizer") {
        return organizer?.verified_status === "verified";
      }
      return true;
    });
  },

  constructUserProfileFromParts(
    user: RawUser,
    player: RawPlayer | null,
    organizer: RawOrganizer | null,
    roles: unknown,
    profile: RawProfile | undefined,
    birthDate: string | undefined,
  ): UserProfile {
    const finalRoles = this.determineUserRoles(roles, organizer);

    return {
      id: user.id || profile?.id || "",
      email: user.email || "",
      emailVerified:
        user.emailVerified === true || profile?.emailVerified === true,
      fullName: user.full_name || user.name || profile?.full_name || "",
      phoneNumber: user.phoneNumber
        ? user.phoneNumber
        : profile?.phoneNumber || undefined,
      phoneNumberVerified:
        user.phoneNumberVerified === true ||
        profile?.phoneNumberVerified === true,

      // Player Info
      nickname: player?.nickname || undefined,
      avatarUrl: player?.avatar_url
        ? getR2PublicUrl(player.avatar_url)
        : undefined,

      // Organizer Info
      organizerName: organizer?.name || undefined,
      organizerDisplayName:
        organizer?.display_name || organizer?.name || undefined,
      organizerAvatarUrl: organizer?.logo_url
        ? getR2PublicUrl(organizer.logo_url)
        : undefined,

      // Roles - STRICT SANITIZATION
      roles: finalRoles,

      // Raw Data
      authUser: user as User,
      player: player ? (player as Player) : undefined,
      organizer: organizer ? (organizer as Organizer) : undefined,

      // Prioritize user table fields if profile is missing (merged tables)
      city: user.city ? user.city : profile?.city || undefined,
      ward: user.ward ? user.ward : profile?.ward || undefined,
      gender: user.gender ? user.gender : profile?.gender || undefined,
      birthDate,

      // Legacy / Compat
      userInfoWithRoles: { ...profile, ...user, roles },
    };
  },

  constructUserProfile(
    user: RawUser,
    player: RawPlayer | null,
    organizer: RawOrganizer | null,
    roles: unknown,
    profile?: RawProfile,
  ): UserProfile {
    let birthDate: string | undefined;
    if (user.birth_date) {
      birthDate =
        user.birth_date instanceof Date
          ? user.birth_date.toISOString()
          : String(user.birth_date);
    } else {
      birthDate = profile?.birth_date || undefined;
    }

    return this.constructUserProfileFromParts(
      user,
      player,
      organizer,
      roles,
      profile,
      birthDate,
    );
  },

  /**
   * Initial Profile Setup
   */
  async setupProfile(data: {
    fullName: string;
    nickname: string;
    dateOfBirth: string;
    gender: string;
    city: string;
    ward: string;
    avatar?: string;
  }): Promise<ProfileApiResult> {
    try {
      // Sanitize Avatar
      let finalAvatar = data.avatar;
      if (finalAvatar?.startsWith("blob:")) {
        console.warn("[ProfileService] Blob URL detected in setup. Stripping.");
        finalAvatar = undefined;
      }

      const { data: responseData } = await usersControllerSetupProfile({
        ...data,
        avatar: finalAvatar,
      });

      return {
        success: true,
        message: "Thiết lập hồ sơ thành công.",
        data: responseData,
      };
    } catch (error: unknown) {
      const err = error as {
        message: string;
        response?: { data?: { message?: string } };
      };
      return {
        success: false,
        message: err.response?.data?.message || "Không thể thiết lập hồ sơ.",
        error: err.message,
      };
    }
  },

  /**
   * Unified Update Profile (Router)
   * Automatically calls relevant endpoints based on dirty fields
   */
  async updateProfile(data: UpdateProfileRequest): Promise<ProfileApiResult> {
    try {
      console.log("[ProfileService] Updating profile...", Object.keys(data));

      // 1. Common Fields -> Update User Profile
      if (
        hasAnyOwnField(data, [
          "fullName",
          "phone",
          "email",
          "dateOfBirth",
          "gender",
          "city",
          "ward",
        ])
      ) {
        await usersControllerUpdateProfile({
          fullName: data.fullName,
          phone: data.phone,
          email: data.email,
          dateOfBirth: data.dateOfBirth,
          gender: data.gender,
          city: data.city,
          ward: data.ward,
        });
      }

      // 2. Player Fields -> Update Player Profile
      let safeAvatar = data.avatarUrl?.trim();
      if (
        !safeAvatar ||
        safeAvatar === "null" ||
        safeAvatar === "undefined" ||
        safeAvatar.startsWith("blob:")
      ) {
        safeAvatar = undefined; // Drop blob
      }

      if (hasAnyOwnField(data, ["nickname", "bio", "avatarUrl"])) {
        await usersControllerUpdatePlayerProfile({
          nickname: data.nickname,
          avatar: safeAvatar,
          bio: data.bio,
        });
      }

      // 3. Organizer Fields -> Update Organizer Profile
      if (
        hasAnyOwnField(data, [
          "organizerName",
          "organizerDisplayName",
          "organizationType",
          "contactEmail",
          "contactPhone",
          "address",
          "contactPerson",
          "contactPosition",
          "organizerLogoUrl",
          "licenseType",
          "verificationNote",
          "organizerDescription",
          "slug",
          "fanpageUrl",
          "websiteUrl",
        ])
      ) {
        await usersControllerUpdateOrganizerProfile({
          organizationName: data.organizerName,
          displayName: data.organizerDisplayName,
          organizationType: data.organizationType,
          avatar: data.organizerLogoUrl,
          contactEmail: data.contactEmail,
          contactPhone: data.contactPhone,
          address: data.address,
          contactPerson: data.contactPerson,
          contactPosition: data.contactPosition,
          licenseType: data.licenseType,
          verificationNote: data.verificationNote,
          description: data.organizerDescription,
          slug: data.slug,
          fanpageUrl: data.fanpageUrl,
          websiteUrl: data.websiteUrl,
        });
      }

      return {
        success: true,
        message: "Cập nhật thành công",
      };
    } catch (error: unknown) {
      console.error("[ProfileService] Update error:", error);
      const err = error as {
        message: string;
        response?: { data?: { message?: string } };
      };
      return {
        success: false,
        message: err.response?.data?.message || "Cập nhật thất bại",
        error: err.message,
      };
    }
  },

  /**
   * Check Nickname Availability
   */
  async checkNicknameExists(nickname: string): Promise<boolean> {
    try {
      // Expecting response body: { data: { exists: boolean } }
      // T = { data: { data: { exists: boolean } } }
      const { data } = await customInstance<{
        data: { data: { exists: boolean } };
      }>("/users/player/check-nickname", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nickname }),
      });
      return !!data?.data?.exists;
    } catch {
      return false;
    }
  },

  /**
   * Upload Avatar (Direct to R2 via Backend Signed URL or Proxy)
   */
  async uploadAvatar(
    file: Blob | File,
    userId: string,
    role: "Player" | "Organizer" = "Player",
  ): Promise<string> {
    try {
      const avatarFile =
        file instanceof File
          ? file
          : new File([file], `avatar-${Date.now()}.png`, {
              type: file.type || "image/png",
            });

      const path = UploadService.generateAvatarPath(userId, role, avatarFile);
      const uploadResult = await UploadService.uploadFile(avatarFile, path, {
        bucket: "public",
        contentType: avatarFile.type,
      });

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || "Avatar upload failed");
      }

      return uploadResult.url || getR2PublicUrl(uploadResult.path) || "";
    } catch (error: unknown) {
      console.error("[ProfileService] Upload avatar error:", error);
      throw error;
    }
  },

  /**
   * Helper to get User by ID
   */
  async getUserProfileById(userId: string): Promise<UserProfile | null> {
    // Optimized: If getting self, use current profile logic
    const currentUser = await this.getCurrentUserProfile();
    if (currentUser.id === userId || currentUser.authUser?.id === userId) {
      return currentUser;
    }

    // Backend endpoint /users/:id/profile missing
    console.warn(
      "[ProfileService] Getting other user profile not fully implemented.",
    );
    return null;
  },

  /**
   * Get formatted Avatar URL
   * Helper to construct R2 path
   */
  getAvatarUrlWithStructure(
    userId: string,
    role: string,
    filename: string,
  ): string {
    if (!filename) return "";
    const trimmed = filename.trim();
    if (
      /^(https?:\/\/|blob:|data:|file:\/\/|content:\/\/|capacitor:\/\/)/i.test(
        trimmed,
      )
    ) {
      return trimmed;
    }

    if (trimmed.startsWith("/")) {
      if (trimmed.startsWith("/assets/")) {
        return trimmed;
      }

      return getR2PublicUrl(trimmed) || trimmed;
    }

    if (
      trimmed.startsWith("avatars/") ||
      trimmed.startsWith("/avatars/") ||
      /^[a-z0-9.-]+\.[a-z]{2,}\/.*$/i.test(trimmed)
    ) {
      return getR2PublicUrl(trimmed) || "";
    }

    const normalizedFileName = trimmed.startsWith("/")
      ? trimmed.slice(1)
      : trimmed;
    const path = `avatars/${userId}/${role}/${normalizedFileName}`;
    return getR2PublicUrl(path) || "";
  },

  /**
   * Get simple Avatar URL (Legacy Helper)
   */
  getAvatarUrl(path: string): string {
    if (!path) return "";
    const trimmed = path.trim();
    if (!trimmed) return "";
    if (
      /^(https?:\/\/|blob:|data:|file:\/\/|content:\/\/|capacitor:\/\/)/i.test(
        trimmed,
      )
    ) {
      return trimmed;
    }

    if (trimmed.startsWith("/assets/")) {
      return trimmed;
    }

    if (trimmed.startsWith("/")) {
      return getR2PublicUrl(trimmed) || trimmed;
    }

    return getR2PublicUrl(trimmed) || "";
  },
};

export default profileService;
